/* Powered by plants. Added by Oleg @ IAKOE.
   <ts-ingredients-spotlight>: an accordion on mobile, a hover spotlight on desktop. One module,
   no imports (see the no-relative-imports rule).

   Mobile: tapping a row opens its body and closes the others (tapping the open one
   closes it). Toggled via the `hidden` attribute, which is also the server-rendered
   no-JS state, so first paint and no-JS both show the first item open.

   Desktop: hovering (or focusing, for keyboards) a row italicises it and swaps the
   left spotlight panel. The row order matches the panel order — both come from the
   same Liquid loop. Hover state also syncs [data-open] so a viewport change from
   desktop to mobile lands with the same item expanded. */

if (!customElements.get('ts-ingredients-spotlight')) {
  customElements.define(
    'ts-ingredients-spotlight',
    class TsIngredientsSpotlight extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-plant-item]'));
        this.spots = Array.from(this.querySelectorAll('[data-plant-spot]'));
        if (!this.items.length) return;

        this._onClick = this.onClick.bind(this);
        this._onOver = this.onOver.bind(this);
        this.addEventListener('click', this._onClick);
        // mouseover (not mouseenter) so delegation works; focusin covers keyboards.
        this.addEventListener('mouseover', this._onOver);
        this.addEventListener('focusin', this._onOver);

        // The first ingredient starts selected (italic name + its spotlight showing),
        // matching the server-rendered visible spot.
        this.activate(this.items[0]);

        // Desktop: quietly warm the other spotlight images once the page itself is
        // done, so the first hover swap doesn't flash an empty frame. The hidden
        // <img>s never load on their own (display:none), so we prime the HTTP cache
        // with Image() fetches - sequentially, to never compete for bandwidth.
        if (window.matchMedia('(min-width: 1024px)').matches) this.warmSpots();
      }

      warmSpots() {
        if (navigator.connection && navigator.connection.saveData) return;
        const run = () => {
          const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 500));
          idle(() => {
            const srcs = this.spots
              .filter((sp) => sp.hidden)
              .map((sp) => sp.querySelector('img'))
              .filter(Boolean)
              .map((img) => img.getAttribute('src'))
              .filter(Boolean);
            srcs.reduce(
              (chain, src) =>
                chain.then(
                  () =>
                    new Promise((done) => {
                      const im = new Image();
                      im.onload = done;
                      im.onerror = done;
                      im.src = src;
                    })
                ),
              Promise.resolve()
            );
          });
        };
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run, { once: true });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('mouseover', this._onOver);
        this.removeEventListener('focusin', this._onOver);
      }

      itemOf(event) {
        const row = event.target.closest('[data-plant-row]');
        if (!row || !this.contains(row)) return null;
        return row.closest('[data-plant-item]');
      }

      /* Mobile accordion. Desktop clicks just re-activate the spotlight (harmless:
         the bodies are display:none there anyway). */
      onClick(event) {
        const item = this.itemOf(event);
        if (!item) return;
        const isOpen = item.hasAttribute('data-open');
        this.items.forEach((it) => this.setOpen(it, false));
        if (!isOpen) this.setOpen(item, true);
        this.activate(item);
      }

      onOver(event) {
        // Hover drives the DESKTOP spotlight only. On touch, the synthetic mouseover
        // fires right before click and the pair double-toggled the accordion (a tap on
        // a closed item opened it on mouseover, then click saw it open and closed it).
        if (!window.matchMedia('(min-width: 1024px)').matches) return;
        const item = this.itemOf(event);
        if (!item) return;
        this.activate(item);
        // Keep the accordion in step so a desktop->mobile resize shows the same item.
        this.items.forEach((it) => this.setOpen(it, it === item));
      }

      setOpen(item, open) {
        item.toggleAttribute('data-open', open);
        const row = item.querySelector('[data-plant-row]');
        const body = item.querySelector('[data-plant-body]');
        if (row) row.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (body) body.hidden = !open;
      }

      activate(item) {
        const idx = this.items.indexOf(item);
        this.items.forEach((it, i) => it.toggleAttribute('data-active', i === idx));
        this.spots.forEach((sp, i) => (sp.hidden = i !== idx));
      }
    }
  );
}
